// EBITDARoleSelector.tsx
import React from 'react';
import { getEBITDARoleColorClass, getEBITDADisplayLabel } from '../../lib/financial/ebitdaUtils';
import { AccountType, EBITDARole } from '../../types';

interface EBITDARoleSelectorProps {
  value: EBITDARole | null;
  onChange: (value: EBITDARole | null) => void;
  accountType: AccountType;
}

const EBITDARoleSelector: React.FC<EBITDARoleSelectorProps> = ({
  value,
  onChange,
  accountType
}) => {
  // Determine if this account type should have EBITDA role options
  const isIncomeStatementItem = ['revenue', 'contra-revenue', 'cogs', 'contra-cogs', 'operating-expense', 
                                'interest-expense', 'income-tax-expense', 'other-expense'].includes(accountType);

  // Only specific income statement accounts are used in EBITDA adjustments
  const canHaveEBITDARole = ['operating-expense', 'interest-expense', 'income-tax-expense'].includes(accountType);

  // If not eligible for EBITDA role, show message
  if (!isIncomeStatementItem || !canHaveEBITDARole) {
    return (
      <div className="text-gray-400 text-xs whitespace-normal flex items-center">
        <svg className="h-3.5 w-3.5 mr-1 flex-shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <span>Not applicable</span>
      </div>
    );
  }

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value ? e.target.value as EBITDARole : null)}
      className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
      aria-label="Select EBITDA Role"
    >
      <option value="">None</option>
      
      {/* Interest expense */}
      {accountType === 'interest-expense' && (
        <option value="interest-expense">Interest Expense</option>
      )}
      
      {/* Income tax expense */}
      {accountType === 'income-tax-expense' && (
        <option value="income-tax-expense">Income Tax Expense</option>
      )}
      
      {/* Operating expenses that might be D&A */}
      {accountType === 'operating-expense' && (
        <>
          <option value="depreciation">Depreciation</option>
          <option value="amortization">Amortization</option>
        </>
      )}
    </select>
  );
};

export default EBITDARoleSelector;
